import { useCallback, useEffect, useMemo } from "react";
import { WSInfiniteQueryResult } from "@ws-react-query";

/**
 * Configuration options for useAllFromInfiniteQuery
 */
export interface UseAllFromInfiniteQueryConfig {
  /**
   * Whether to automatically fetch all pages on mount
   * @default true
   */
  autoFetch?: boolean;
}

/**
 * Extract element type from array type, or return the type itself if not an array
 */
type ExtractArrayElement<T> = T extends readonly (infer U)[] ? U : T;

/**
 * Helper type to flatten array of pages into array of elements
 */
type FlattenPages<TData> = TData extends readonly unknown[]
  ? ExtractArrayElement<TData>[]
  : TData[];

/**
 * Query result type compatible with WSQueries
 */
export type UseAllFromInfiniteQueryResult<TData, TError> = Omit<
  WSInfiniteQueryResult<TData, TError>,
  "data"
> & {
  data: FlattenPages<TData>;
  fetchAll: () => Promise<FlattenPages<TData>>;
};

/**
 * Type guard to check if pages contain arrays that need to be flattened
 */
function isArrayOfArrays(
  pages: readonly unknown[]
): pages is readonly (readonly unknown[])[] {
  return pages.length > 0 && Array.isArray(pages[0]);
}

/**
 * Flatten pages from infinite query into single array
 */
function flattenPages<TData>(
  pages: readonly TData[] | undefined
): FlattenPages<TData> {
  if (!pages || pages.length === 0) {
    return [] as unknown as FlattenPages<TData>;
  }

  // If pages contain arrays, flatten them
  if (isArrayOfArrays(pages)) {
    const flattened = pages.flat();
    return flattened as FlattenPages<TData>;
  }

  // If pages are not arrays, return them as-is (each page is a single item)
  return pages as FlattenPages<TData>;
}

/**
 * Hook to automatically fetch all pages from an infinite query and return a query-like object
 * that can be used with WSQueries
 *
 * @param infiniteQuery - Result from useWSInfiniteQuery hook
 * @param config - Configuration options
 * @returns Query-like object with flattened data compatible with WSQueries
 *
 * @example
 * const queryAllCoverages = useAllFromInfiniteQuery(
 *   useInsuranceCoverages({ filter: { payerId } })
 * );
 *
 * return (
 *   <WSQueries queries={{ queryAllCoverages }}>
 *     {({ queryAllCoveragesData }) => (
 *       <div>{queryAllCoveragesData.map(item => ...)}</div>
 *     )}
 *   </WSQueries>
 * );
 *
 * @example
 * // With manual fetching
 * const queryAllCoverages = useAllFromInfiniteQuery(
 *   useInsuranceCoverages({ filter: { payerId } }),
 *   { autoFetch: false }
 * );
 */
export const useAllFromInfiniteQuery = <TData = unknown, TError = unknown>(
  infiniteQuery: WSInfiniteQueryResult<TData, TError>,
  config: UseAllFromInfiniteQueryConfig = {}
): UseAllFromInfiniteQueryResult<TData, TError> => {
  const { autoFetch = true } = config;
  const { data, hasNextPage, fetchNextPage, isFetching, ...restQuery } =
    infiniteQuery;

  // Automatically fetch all pages
  useEffect(() => {
    if (autoFetch && hasNextPage && !isFetching) {
      fetchNextPage();
    }
  }, [autoFetch, hasNextPage, isFetching, fetchNextPage]);

  // Flatten all pages into single array, memoized to prevent unnecessary re-renders
  const flattenedData = useMemo(() => flattenPages(data?.pages), [data?.pages]);

  // Fetch all remaining pages and return complete data
  const fetchAll = useCallback(async (): Promise<FlattenPages<TData>> => {
    let result = { data: data, hasNextPage };

    // Keep fetching while there are more pages
    while (result.hasNextPage) {
      const fetchResult = await fetchNextPage();
      result = {
        data: fetchResult.data,
        hasNextPage: fetchResult.hasNextPage
      };
    }

    // Return all pages flattened
    return flattenPages(result.data?.pages);
  }, [data, hasNextPage, fetchNextPage]);

  // Return query-like object compatible with WSQueries
  return {
    ...restQuery,
    data: flattenedData,
    hasNextPage,
    fetchNextPage,
    isFetching,
    fetchAll
  };
};
